import { action, makeObservable, observable, runInAction, observe } from 'mobx';
import { getProductList, removeProductReq, editProductReq } from '../api/admin';
import { Product } from '../model/Product';
import CompanyStore from './CompanyStore';

const ROWS_PER_PAGE = 15;

class ProductStore {
  companyStore: CompanyStore;

  list: Product[] = [];

  endReached = false;

  loading = false;

  errorMessage: null | string = null;

  constructor(companyStore: CompanyStore) {
    this.companyStore = companyStore;

    makeObservable(this, {
      list: observable,
      loading: observable,
      fetchInitialProducts: action,
      refreshProducts: action,
      removeProduct: action,
      editProduct: action,
    });
    observe(this.companyStore, 'companyKey', async ({ newValue }) => {
      if (newValue) {
        this.initialize();
      } else {
        this.clear();
      }
    });
  }

  async initialize() {
    if (!this.companyStore.companyKey) {
      return;
    }
    runInAction(() => {
      this.list = [];
      this.fetchInitialProducts();
    });
  }

  clear() {
    this.list = [];
  }

  fetchInitialProducts() {
    if (!this.companyStore.companyKey || this.loading || this.list.length !== 0) {
      return;
    }
    this.loading = true;
    getProductList(this.companyStore.companyKey)
      .then((res) => {
        if (res.length < ROWS_PER_PAGE) {
          this.endReached = true;
        }
        runInAction(() => {
          this.list = res.filter((product) => !product.removed);
        });
      })
      .catch(() => {
        runInAction(() => {
          this.errorMessage = 'Ett fel har inträffat';
        });
      })
      .finally(() => {
        runInAction(() => {
          this.loading = false;
        });
      });
  }

  fetchMoreProducts() {
    if (!this.companyStore.companyKey || this.loading || this.endReached) {
      return;
    }
    runInAction(() => {
      this.loading = true;
    });
    getProductList(this.companyStore.companyKey, this.list.length)
      .then((res) => {
        runInAction(() => {
          if (res.length < 25) {
            this.endReached = true;
          }
          this.list.push(...res.filter((product) => !product.removed));
        });
      })
      .catch(() => {
        runInAction(() => {
          this.errorMessage = 'Ett fel har inträffat';
        });
      })
      .finally(() => {
        runInAction(() => {
          this.loading = false;
        });
      });
  }

  refreshProducts() {
    if (this.loading) {
      return;
    }
    this.list = [];
    this.fetchInitialProducts();
  }

  removeProduct = async (id: string) => {
    if (!this.companyStore.companyKey || !id) {
      return;
    }

    await removeProductReq(this.companyStore.companyKey, id)
      .then(() => {
        runInAction(() => {
          this.list = this.list.filter((product) => product.id !== id);
        });
      })
      .catch((err) => {
        this.errorMessage = err.message;
      });
  };

  editProduct = async (product: Product) => {
    if (!this.companyStore.companyKey) {
      return;
    }

    await editProductReq(this.companyStore.companyKey, product)
      .then((res: Product) => {
        runInAction(() => {
          const index = this.list.findIndex((item) => item.id === res.id);

          if (index !== -1) {
            this.list[index] = {
              ...this.list[index],
              ...res,
            };
          }
        });
      })
      .catch((err) => {
        runInAction(() => {
          this.errorMessage = err.message;
        });
      });
  };
}

export default ProductStore;
